import React, {useContext, useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {
    IButtonProps,
    IChartDataProps,
    IContentFiltersProps,
    IFilterItemProps,
    IFilterProps,
    IPageOptionProps,
    IPageSelectProps,
    IPageTitleAndFiltersProps,
    IQuestionProps,
    ITableRowProps,
    ITemplateDashboardFeedbackProps,
    ITemplateDashboardHeatmapProps,
    ITemplateDashboardQuestionsProps,
    ITemplateGeneralCardProps,
    ITemplateGeneralRowProps,
    ITemplateOverviewCardProps,
    ITemplatePlainTextCardProps,
    Main,
    ModalProvider,
    Notification,
    TemplateDashboard,
    TFilterValue,
    THeatLevel,
    TProgressColor,
} from '../../../patterns';
import {StoreContext} from '../base/root-store';
import {
    AnswerWithComment,
    AnswerWithCommentOnQuestionCard,
    BusinessUnitData,
    DashboardContext,
    DashboardData,
    DashboardPageTab,
    Datapoint,
    HeatmapRow,
    QuestionCard,
    TeamResultsStore,
} from './team-results-store';
import {IFeedbackItemProps} from '../../../patterns/components/feedback/feedback';
import {formatDateRange, formatToDayAndMonth, periodIdFromRange, rangeFromPeriodId} from '../base/dateUtils';
import {WebappBase} from './WebappBase';
import {ScoreTooltip} from '../base/ScoreTooltip';
import {IHeatmapCellProps, IHeatmapRowProps} from '../../../patterns/components/heatmap/heatmap';
import {UserStore} from '../user/user-store';
import {IAnswerProps} from '../../../patterns/components/feedback-answers/feedback-answers';
import {FeedbackHelpModal} from './FeedbackHelpModal';
import {CompanyHelpModal} from './CompanyHelpModal';
import {CategoryHelpModal} from './CategoryHelpModal';
import {OverviewHelpModal} from './OverviewHelpModal';
import {HeatmapHelpModal} from './HeatmapHelpModal';
import {LocationHelpModal} from './LocationHelpModal';
import {Capacitor} from '@capacitor/core';
import {ServiceLengthHelpModal} from './ServiceLengthHelpModal';
import {RolesHelpModal} from './RolesHelpModal';
import {TagsHelpModal} from './TagsHelpModal';
import {QuestionsHelpModal} from './QuestionsHelpModal';
import {SendResultsHelpModal} from './SendResultsHelpModal';
import {ISelectDatepickerProps} from '../../../patterns/components/forms/select/select';
import {DateRange} from 'react-day-picker';
import {Language} from '../base/label-store';
import {getCategoryLanguage, getCategoryName, isCustomCategory} from '../categories/CategoriesUtil';

export const TeamDashboardPage = (): JSX.Element => <Dashboard context="dashboard"/>;

export const EmailRaport = (): JSX.Element => <Dashboard context="email-report"/>;

const Dashboard = observer((props: { context: DashboardContext }): JSX.Element => {
    const store = useContext(StoreContext).teamResultsStore;
    const userStore = useContext(StoreContext).userStore;

    useEffect(() => {
        if (props.context !== 'email-report') {
            store.onEnter(props.context);
        }
    }, []);

    if (store.state === 'error' && store.errorReason === 'user-deleted') {
        return <Main showBackground={true}>
            <Notification
                text={store.label('common.user.deleted.error-message')}
                defaultIsOpen={true}
                type="info"
            />
        </Main>;
    }

    if (store.variables.expired) {
        return <Main showBackground={true}>
            <Notification
                title={store.label('chat.expired.title')}
                text={store.label('chat.expired.description-generic')}
                defaultIsOpen={true}
                type="info"
            />
        </Main>;
    }

    return (
        <WebappBase
            store={store}
            title={store.label('dashboard.title')}
            renderChildren={(): React.ReactNode => (
                <React.Fragment>
                    <ModalProvider
                        isOpen={store.variables.openModal !== undefined}
                        onToggle={(): void => store.closeModal()}>
                        {store.variables.openModal === 'help-overview' && <OverviewHelpModal/>}
                        {store.variables.openModal === 'help-feedback' && <FeedbackHelpModal/>}
                        {store.variables.openModal === 'help-category' && <CategoryHelpModal/>}
                        {store.variables.openModal === 'help-company' && <CompanyHelpModal/>}
                        {store.variables.openModal === 'help-heatmap' && <HeatmapHelpModal/>}
                        {store.variables.openModal === 'help-location' && <LocationHelpModal/>}
                        {store.variables.openModal === 'help-service-length' && <ServiceLengthHelpModal/>}
                        {store.variables.openModal === 'help-role' && <RolesHelpModal/>}
                        {store.variables.openModal === 'help-tag' && <TagsHelpModal/>}
                        {store.variables.openModal === 'help-questions' && <QuestionsHelpModal/>}
                        {store.variables.openModal === 'help-sendResults' && <SendResultsHelpModal/>}
                    </ModalProvider>
                    <TemplateDashboard
                        pageTitleAndFilters={titleAndFilters(store, userStore)}
                        history={overviewCardProps(store)}
                        feedback={feedbackProps(store, userStore)}
                        categories={categoryProps(store, userStore)}
                        company={companyProps(store, userStore)}
                        locations={locationProps(store, userStore)}
                        lengthOfService={lengthOfServiceProps(store, userStore)}
                        roles={rolesProps(store, userStore)}
                        tags={tagsProps(store, userStore)}
                        heatmap={heatmapProps(store, userStore)}
                        questions={questionProps(store, userStore)}
                        adHocQuestions={adHocQuestionProps(store, userStore)}
                        noData={tooFewEmployeesInFilterSelectionAlert(store)}
                    />
                </React.Fragment>
            )}
        />
    );
});

const titleAndFilters = (teamStore: TeamResultsStore, userStore: UserStore): IPageTitleAndFiltersProps => ({
    title: teamStore.label('dashboard.title'),
    dashboardSwitcher: pageSwitcherProps(teamStore, userStore),
    actions: Capacitor.getPlatform() === 'web' ? downloadActionsProps(teamStore, userStore) : undefined,
    contentFilters: contentFilterProps(teamStore, userStore),
    showEmailButton: true,
});

const pageSwitcherProps = (store: TeamResultsStore, userStore: UserStore): IFilterProps | undefined => {
    const isAdminOrManager = userStore.payload?.hasManagerRights === true || userStore.payload?.isAdmin === true;
    const isPersonalDashBoardEnabled = userStore.payload?.isPersonalDashboardEnabled === true;
    const allPageSwitcherItems: IFilterItemProps[] = [
        {
            id: 'team-results',
            name: 'filter',
            label: store.label('dashboard.switcher.team-results'),
            value: 'team-results',
        },
        {
            id: 'new-hires',
            name: 'filter',
            label: store.label('dashboard.switcher.new-hires'),
            value: 'new-hires',
            count: userStore.payload?.peopleInOnboardingCount !== undefined && userStore.payload?.hasManagerRights === true ? userStore.payload?.peopleInOnboardingCount : undefined,
        },
        {
            id: 'my-results',
            name: 'filter',
            label: store.label('dashboard.switcher.my-results'),
            value: 'my-results',
            isRedBackground: true,
        },
    ];

    let eligibleSwitcherItems: IFilterItemProps[];

    if (isPersonalDashBoardEnabled && isAdminOrManager) {
        eligibleSwitcherItems = allPageSwitcherItems;
    } else if (isPersonalDashBoardEnabled && !isAdminOrManager) {
        eligibleSwitcherItems = allPageSwitcherItems.filter((item) => item.id !== 'new-hires');
    } else if (!isPersonalDashBoardEnabled && isAdminOrManager) {
        eligibleSwitcherItems = allPageSwitcherItems.filter((item) => item.id !== 'my-results');
    } else {
        eligibleSwitcherItems = [];
    }

    return eligibleSwitcherItems.length !== 0 ? ({
        label: 'Filter label',
        onChange: (value: TFilterValue): void => {
            store.onDashboardTabChange(value as DashboardPageTab);
        },
        value: store.variables.openTab,

        items: eligibleSwitcherItems,
    }) : undefined;
};

const contentFilterProps = (store: TeamResultsStore, userStore: UserStore): IContentFiltersProps => ({
    filterValues: [
        {oneFilterValue: categoriesFilterProps(store, userStore)},
        {oneFilterValue: departmentsFilterProps(store)},
        {oneFilterValue: userStore.payload?.hasManagerRights === true || userStore.payload?.isAdmin === true ? employeeSegmentFilterProps(store) : undefined},
        {oneFilterValue: store.variables.context === 'dashboard' ? compareToFilterProps(store) : undefined},
        {oneFilterValue: timeframeFilterProps(store)},
        {oneFilterValue: locationFilterProps(store)},
        {oneFilterValue: lengthOfServiceFilterProps(store)},
        {oneFilterValue: roleFilterProps(store)},
        {oneFilterValue: tagFilterProps(store)},
        {oneFilterValue: questionTextProps(store)},
    ],
});

const getShortCategoryNameForHeatMap = (store: TeamResultsStore, userLanguage: Language | undefined, catId: string): string => {
    const category = catId === 'ALL'
        ? store.categories?.find((c) => c.categoryName === 'ALL')
        : store.categories?.find((cat) => cat.id === catId);

    if (!category || !userLanguage) {
        return '';
    }

    const categoryName = getCategoryLanguage(userLanguage, category);
    const normalized = categoryName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    if (userLanguage === 'ja') {
        return normalized.slice(0, 3);
    } else {
        const lettersOnly = normalized.replace(/[^\p{L}\s]/gu, '');
        const words = lettersOnly.trim().split(/\s+/).slice(0, 3);

        return words.map((word) => word.charAt(0)).join('').toUpperCase();
    }
};

const getDefaultCategoryName = (store: TeamResultsStore, catId: string | undefined): string => {
    const category = store?.categories?.find((cat) => cat.id === catId);

    return category?.categoryName ?? '';
};

const categoriesFilterProps = (store: TeamResultsStore, userStore: UserStore): IPageSelectProps | undefined => {
    const filterOptions = store.payload?.filter.categories;

    if (!filterOptions || filterOptions.length === 0) {
        return undefined;
    }

    const options = filterOptions.map((filterOption) => ({
        label: getCategoryName(userStore.payload!.language, filterOption.id, store.categories),
        value: filterOption.id,
    }));

    return ({
        label: store.label('dashboard.filter.categories.title'),
        id: 'dashboard-categories',
        defaultValue: filterOptions.filter((filterOption) => filterOption.selected)[0].id,
        search: {
            id: 'categories-search',
            name: 'categories',
            label: store.label('common.filter.search.label.categories'),
            focusLabel: store.label('common.filter.search.focuslabel'),
        },
        options,
        onChange: ((filterOptionsId) => store.onCategoryChange('' + filterOptionsId)),
    } as IPageSelectProps);
};

const departmentsFilterProps = (store: TeamResultsStore): IPageSelectProps | undefined => {
    const filterOptions = store.payload!.filter.businessUnits;

    if (!filterOptions || filterOptions.length === 0) {
        return undefined;
    }

    const options = filterOptions.map((filterOption) => ({
        label: filterOption.label,
        value: filterOption.id,
    } as IPageOptionProps));

    return ({
        label: store.label('common.filter.department.title'),
        name: 'Department',
        id: 'dashboard-department',
        defaultValue: filterOptions.filter((filterOption) => filterOption.selected)[0].id,
        search: {
            id: 'department-search',
            name: 'department',
            label: store.label('common.filter.search.label'),
            focusLabel: store.label('common.filter.search.focuslabel'),
        },
        options,
        onChange: (filterOptionsId): void => store.onBusinessUnitChange('' + filterOptionsId),
        type: undefined,
    });
};

const employeeSegmentFilterProps = (store: TeamResultsStore): IPageSelectProps | undefined => {
    const options = store.payload!.filter.employeeSegment.map(
        (filterOption) => ({
            label: store.label(`dashboard.filter.employee-segment.${filterOption.id}`),
            value: filterOption.id,
            count: filterOption.count,
        }));
    const defaultValue = store.payload!.filter.employeeSegment.filter((filterOption) => filterOption.selected)[0].id;

    // Some other filter options change the employeeSegment filter options so the values that were selected disappear - so set the defaultValue as selected if the current value is not in the options list
    if (store.payload!.filter.employeeSegment.length > 0 && !!store.variables.employeeSegment && !store.payload!.filter.employeeSegment.map((x) => x.id).includes(store.variables.employeeSegment)) {
        store.onEmployeeSegmentChange(defaultValue);
    }

    if (options.length <= 1) {
        return undefined;
    } else {

        return ({
            label: store.label('dashboard.filter.employee-segment.title'),
            name: 'employee-segment',
            id: 'employee-segment',
            defaultValue: store.payload!.filter.employeeSegment.filter((filterOption) => filterOption.selected)[0].id,
            options,
            onChange: ((filterOptionId) => store.onEmployeeSegmentChange('' + filterOptionId)),
        } as IPageSelectProps
        );
    }
};

const compareToFilterProps = (store: TeamResultsStore): IPageSelectProps => {
    const filterOptions = store.payload!.filter.compareTo;

    const options = filterOptions.map((filterOption) => ({
        label: filterOption.label,
        value: filterOption.id,
    } as IPageOptionProps));

    return ({
        label: store.label('common.filter.compareto.title'),
        id: 'dashboard-compare',
        defaultValue: filterOptions.find((option) => option.selected)?.id ?? filterOptions[0]?.id,
        search: {
            id: 'Compare-to-search',
            name: 'Compare-to',
            label: store.label('common.filter.search.label'),
            focusLabel: store.label('common.filter.search.focuslabel'),
        },
        options,
        onChange: (filterOptionsId): void => store.onCompareToChange('' + filterOptionsId),
    } as IPageSelectProps);
};

const timeframeFilterProps = (store: TeamResultsStore): IPageSelectProps => {
    const filterOptions = store.payload!.filter.periods;

    const options = filterOptions.map((filterOption) => ({
        label: store.timeframeFilterLabels(filterOption),
        value: filterOption.id,
    }));

    return ({
        label: store.label('common.filter.period.title'),
        id: 'dashboard-time',
        defaultValue: store.variables.isCustomDate ? undefined : filterOptions.find((filterOption) => filterOption.selected)?.id,
        options,
        datepicker: getDatepickerProps(store),
        onChange: ((filterOptionId: string) => {
            store.setCustomDateSelected(false);
            store.onPeriodChange(filterOptionId);
        }),
    } as IPageSelectProps);
};

const getDatepickerProps: (store: TeamResultsStore) => ISelectDatepickerProps = (store: TeamResultsStore) => ({
    datepickerProps: {
        format: 'dd.MM.yyyy',
        type: 'range',
        defaultValue: {from: undefined, to: undefined} as DateRange,
        field: [
            {
                label: store.label('user-menu.datepicker.from'),
                id: 'datepicker-from',
                name: 'datepicker-from',
            },
            {
                label: store.label('user-menu.datepicker.to'),
                id: 'datepicker-to',
                name: 'datepicker-to',
            },
        ],
    },
    onChange: (range): void => {
        const rangeModifierValue = (range as DateRange);

        if (rangeModifierValue?.to && rangeModifierValue?.from) {
            const value = periodIdFromRange(rangeModifierValue);

            store.setCustomDateSelected(true);
            store.onPeriodChange(value);
        }
    },
    customDateDisplayValue: store.variables.isCustomDate && store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : undefined,
});

const locationFilterProps = (store: TeamResultsStore): IPageSelectProps | undefined => {

    if (store.payload!.filter.locations === undefined) {

        return undefined;
    } else {
        const filterOptions = store.payload!.filter.locations;

        const options = filterOptions.map((filterOption) => ({
            label: store.label(`common.location.${filterOption.id}`),
            value: filterOption.id,
        }));

        return ({
            label: store.label('common.filter.location.title'),
            id: 'dashboard-location',
            defaultValue: filterOptions.filter((filterOption) => filterOption.selected)[0].id,
            options,
            onChange: ((filterOptionId) => store.onLocationChange('' + filterOptionId)),
        } as IPageSelectProps);
    }
};

const roleFilterProps = (store: TeamResultsStore): IPageSelectProps | undefined => {

    if (store.payload!.filter.roles === undefined) {

        return undefined;
    } else {
        const filterOptions = store.payload!.filter.roles;
        const options = filterOptions.map((filterOption) => ({
            label: filterOption.id === 'ALL' ? store.label('common.roles.all') : `${filterOption.id}`,
            value: filterOption.id,
        }));

        return ({
            label: store.label('dashboard.filter.role.title'),
            id: 'dashboard-role-filter',
            defaultValue: filterOptions.filter((filterOption) => filterOption.selected)[0].id,
            search: {
                id: 'filter-role-search',
                name: 'filter-role',
                label: store.label('dashboard.filter.search.label.role'),
                focusLabel: store.label('dashboard.filter.search.focuslabel.role'),
            },
            options,
            onChange: ((filterOptionId) => store.onRoleChange('' + filterOptionId)),
        } as IPageSelectProps);
    }
};

const lengthOfServiceFilterProps = (store: TeamResultsStore): IPageSelectProps | undefined => {

    if (store.payload!.filter.lengthOfService === undefined) {

        return undefined;
    } else {
        const filterOptions = store.payload!.filter.lengthOfService;
        const options = filterOptions.map((filterOption) => ({
            label: store.label(`common.service-length.${filterOption.id}`),
            value: filterOption.id,
        }));

        return ({
            label: store.label('dashboard.filter.service-length.title'),
            id: 'dashboard-service-length-filter',
            defaultValue: filterOptions.filter((filterOption) => filterOption.selected)[0].id,
            options,
            onChange: ((filterOptionId) => store.onLengthOfServiceChange('' + filterOptionId)),
        } as IPageSelectProps);
    }
};

const questionTextProps = (store: TeamResultsStore): IPageSelectProps | undefined => {

    if (store.payload!.filter.questionId === undefined) {
        return undefined;
    } else {
        const filterOptions = store.payload!.filter.questionId;
        const options = filterOptions.map((filterOption) => ({
            label: filterOption.label === 'ALL' ? store.label('common.questions.all') : filterOption.label,
            value: filterOption.id,
        }));

        const defaultValue = filterOptions.filter((filterOption) => filterOption.selected)[0]?.id;

        // Some other filter options change the question filter options so the values that were selected disappear - so set the defaultValue as selected if the current value is not in the options list
        if (store.payload!.filter.questionId.length > 0 && !!store.variables.questionId && !store.payload!.filter.questionId.map((x) => x.id).includes(store.variables.questionId)) {
            store.onQuestionOptionChange(defaultValue);
        }

        return ({
            label: store.label('dashboard.filter.question.title'),
            id: 'dashboard-question-filter',
            defaultValue,
            search: {
                id: 'question-search',
                name: 'filter-question',
                label: store.label('dashboard.filter.search.label.question'),
                focusLabel: store.label('dashboard.filter.search.label.tag'),
            },
            options,
            onChange: ((filterOptionId) => store.onQuestionOptionChange('' + filterOptionId)),
        } as IPageSelectProps);
    }
};

const tagFilterProps = (store: TeamResultsStore): IPageSelectProps | undefined => {

    if (store.payload!.filter.tags === undefined) {

        return undefined;
    } else {
        const filterOptions = store.payload!.filter.tags;
        const options = filterOptions.map((filterOption) => ({
            label: filterOption.id === 'ALL' ? store.label('common.tags.all') : `${filterOption.id}`,
            value: filterOption.id,
        }));

        return ({
            label: store.label('dashboard.filter.tag.title'),
            id: 'dashboard-tag-filter',
            defaultValue: filterOptions.filter((filterOption) => filterOption.selected)[0].id,
            search: {
                id: 'filter-tag-search',
                name: 'filter-tag',
                label: store.label('dashboard.filter.search.label.tag'),
                focusLabel: store.label('dashboard.filter.search.focuslabel.tag'),
            },
            options,
            onChange: ((filterOptionId) => store.onTagChange('' + filterOptionId)),
        } as IPageSelectProps);
    }
};

const downloadActionsProps = (store: TeamResultsStore, userStore: UserStore): IButtonProps[] | undefined => {
    const {payload} = userStore;
    const employeeId = payload?.employeeId;
    let selectedBusinessUnit : string | undefined;

    if (payload?.hasManagerRights || payload?.isAdmin) {
        selectedBusinessUnit = store.payload?.filter.businessUnits.find((businessUnit) => businessUnit.selected)?.id;
    }
    if (employeeId === undefined) {
        return undefined;
    }

    const isManagedBusinessUnitRequested = (selectedBusinessUnit !== undefined &&
        userStore.payload?.businessUnits?.map((it) => it.id)?.includes(selectedBusinessUnit)) ?? false;

    const buttons: IButtonProps[] = [
        payload?.isAdmin && {
            text: store.label('dashboard.data-download'),
            iconLeft: 'download-arrow',
            onClick: (): Promise<void> => store.openDataDownloadPage(employeeId),
        },
        ((payload?.isAdmin || payload?.hasManagerRights) && isManagedBusinessUnitRequested) && {
            text: store.label('dashboard.send-manager-report'),
            iconLeft: 'mail-out',
            onClick: (): Promise<void> => store.sendManagerReport(employeeId, selectedBusinessUnit),
        },
    ].filter(Boolean) as IButtonProps[];

    return buttons.length > 0 ? buttons : undefined;
};

const overviewCardProps = (store: TeamResultsStore): ITemplateOverviewCardProps | undefined => {
    if (store.payload!.overview === undefined) {
        return undefined;
    } else {
        const chartData: IChartDataProps[] = store.payload!.overview.chart.map((chartpoint, index): IChartDataProps => ({
            name: index + 1,
            value: chartpoint.value.scorePercentage,
            compare: chartpoint.compareTo.scorePercentage ,
            activity: chartpoint.value.participationPercentage,
        })).filter((_, index): boolean => store.payload!.overview!.chart[index].value.answered !== 0)
        ;

        const helpButtonProps: IButtonProps[] = [
            {
                text: '',
                color: 'white',
                icon: 'info',
                onClick: (): void => store.openOverviewHelpModal(),
            },
        ];

        return {
            title: store.label('dashboard.overview.title'),
            actions: helpButtonProps,
            chart: {
                aspect: 2.2,
                tickFormatter: (tick: number): string => formatToDayAndMonth(store.payload!.overview!.chart[tick - 1].period.to),
                id: 'chart-1',
                data: chartData,
                areas: [
                    {
                        key: 'value',
                    },
                ],
                lines: [
                    {key: 'activity', color: 'blue', strokeWidth: 2},
                    {key: 'compare', color: 'gray', strokeWidth: 2, strokePattern: '12 8'},
                ],
                showTooltip: true,
                // eslint-disable-next-line react/display-name
                tooltipFormatter: (index: number): JSX.Element => (
                    <ScoreTooltip
                        period={store.payload!.overview!.chart[index - 1].period}
                        name={store.payload!.tooltip.businessUnitName}
                        compareToName={store.payload!.tooltip.businessUnitName === store.payload!.tooltip.compareToName ? undefined : store.payload!.tooltip.compareToName}
                        value={store.payload!.overview!.chart[index - 1].value}
                        compareTo={store.payload!.tooltip.businessUnitName === store.payload!.tooltip.compareToName ? undefined : store.payload!.overview!.chart[index - 1].compareTo}
                    />
                ),
            },
            bars: [
                {
                    icon: 'users',
                    color: 'blue',
                    label: store.label('dashboard.overview.activity', {
                        answers_percentage: store.payload!.overview.total.participationPercentage.toString(),
                    }),
                    percent: store.payload!.overview.total.participationPercentage,
                },
                {
                    icon: 'like',
                    color: 'green',
                    label: store.label('dashboard.overview.score', {score_percentage: store.payload!.overview.total.scorePercentage.toString()}),
                    percent: store.payload!.overview.total.scorePercentage,
                },
            ],
            notEnoughDataText: store.payload!.overview.chart.length < 2 ? store.label('dashboard.not-enough-data') : undefined,
        };
    }
};

const feedbackProps = (store: TeamResultsStore, userStore: UserStore): ITemplateDashboardFeedbackProps | undefined => {
    if (store.payload!.feedback === undefined) {
        return undefined;
    } else {
        const questions: IFeedbackItemProps[] = store.payload!.feedback.questionFeedback.map((question) => {
            const answers = question.answers.map((answer: AnswerWithComment): IAnswerProps => {
                const canManagerClickHasSeen = userStore.payload?.hasManagerRights && userStore.payload!.businessUnitId === answer.questionBusinessUnitId;
                const checkIsVisibleToManagersAndAdmins = canManagerClickHasSeen || userStore.payload?.isAdmin ? store.hasSeen(answer) : undefined;
                const checkIsVisible = store.hasSeen(answer) ? store.hasSeen(answer) : undefined;

                return ({
                    reactionType: answer.answer === 'null' ? 'talk-bubble' : 'thumb',
                    direction: answer.answer === '100' || answer.answer === '75' ? 'up' : 'down',
                    text: store.translateComment(answer),
                    onHasSeenCheck: canManagerClickHasSeen || userStore.payload?.isAdmin ? (checked): void => {
                        store.managerChecksComment(answer.questionId, checked);
                    } : undefined,
                    hasSeen: checkIsVisibleToManagersAndAdmins !== undefined ? checkIsVisibleToManagersAndAdmins : checkIsVisible,
                    titleTooltip: store.hasSeen(answer) ? store.label('dashboard.feedback.manager-has-seen') : store.label('dashboard.feedback.manager-should-see'),
                    commentToolTip: (userStore.payload?.isAdmin || userStore.payload?.hasManagerRights) ? answer.questionBusinessUnitName : undefined,
                });
            });

            return {
                title: question.categoryId !== undefined ? getCategoryName(userStore.payload!.language, question.categoryId, store.categories) : store.label(`common.category.ad-hoc`),
                question: question.questionText,
                answers: {answers},
                count: answers.length,
                id: question.catalogQuestionId,
            } as IFeedbackItemProps;
        });

        const buttonProps: IButtonProps[] = [
            ...((userStore.payload?.hasManagerRights || userStore.payload?.isAdmin) && userStore.payload?.isMentorEnabled === true
                ? [{
                    color: 'white' as const,
                    icon: 'robot-happy',
                    onClick: async () => await store.routeToMentorForSummary('feedback'),
                    tooltip: store.label('mentor.feedback.summary-tooltip'),
                    isLoading: store.isTranslationButtonLoading,
                }]
                : []),
            {
                color: 'white' as const,
                icon: 'translate',
                onClick: () : Promise<void> => store.onTranslateCommentsButtonClicked(),
                tooltip: store.translateCommentsEnabled ? store.label('dashboard.feedback.show-original-comments') : store.label('dashboard.feedback.translate-comments'),
                isLoading: store.isTranslationButtonLoading,
            },
            {
                text: '',
                color: 'white' as const,
                icon: 'info',
                onClick: (): void => store.openFeedbackHelpModal(),
            },
        ];

        return {
            title: store.label('dashboard.feedback.title'),
            actions: buttonProps,
            data: {
                items: questions.length === 0 ? {
                    type: 'info',
                    text: store.label('dashboard.not-enough-data'),
                } : questions,
            },
            more: {showExpand: true},
            businessUnit: store.payload!.filter.businessUnits.find((filterOption) => filterOption.selected)?.label,
            period: store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : '',
        };
    }
};

const categoryProps = (store: TeamResultsStore, userStore: UserStore): ITemplateGeneralCardProps | undefined => {
    if (store.payload!.categories?.categories === undefined) {
        return undefined;
    } else {
        const categoryRows = store.payload!.categories.categories
            .filter((categoryData) => categoryData.barpoint.value.answered !== 0)
            .map((row) => dataRow(store, row, 'category', userStore));

        const helpButtonProps: IButtonProps[] = [
            {
                text: '',
                color: 'white' as const,
                icon: 'info',
                onClick: (): void => store.openCategoryHelpModal(),
            },
            ...((userStore.payload?.hasManagerRights || userStore.payload?.isAdmin) && userStore.payload?.isMentorEnabled === true
                ? [{
                    color: 'white' as const,
                    icon: 'robot-happy',
                    onClick: async () => await store.routeToMentorForSummary('category'),
                    tooltip: store.label('mentor.category.summary-tooltip'),
                    isLoading: store.isTranslationButtonLoading,
                }]
                : []),
        ];

        return ({
            title: store.label('dashboard.categories.title'),
            actions: helpButtonProps,
            table: {
                columns: [
                    {
                        header: store.label('dashboard.categories.header.category'),
                        width: '100%',
                        isSortable: true,
                    },
                    {
                        header: store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : '',
                        isSortable: true,
                    },
                    {
                        header: store.label('dashboard.common.header.change'),
                        isSortable: true,
                    },
                    {
                        header: store.label('dashboard.common.header.activity'),
                        isSortable: true,
                    },
                ],
                rows: store.payload!.overview!.total.answered < 50 ? {
                    type: 'info',
                    text: store.label('dashboard.not-enough-data'),
                } : categoryRows,
            },
            more: {showExpand: true},
        });
    }
};

const locationProps = (store: TeamResultsStore, userStore: UserStore): ITemplateGeneralCardProps | undefined => {
    if (store.payload!.locations === undefined) {
        return undefined;
    } else {
        const locationRows = store.payload!.locations.locations
            .map((row) => dataRow(store, row, 'location', userStore));

        const helpButtonProps: IButtonProps[] = [
            {
                text: '',
                color: 'white' as const,
                icon: 'info',
                onClick: (): void => store.openLocationHelpModal(),
            },
            ...((userStore.payload?.hasManagerRights || userStore.payload?.isAdmin) && userStore.payload?.isMentorEnabled === true
                ? [{
                    color: 'white' as const,
                    icon: 'robot-happy',
                    onClick: async () => await store.routeToMentorForSummary('country'),
                    tooltip: store.label('mentor.country.summary-tooltip'),
                    isLoading: store.isTranslationButtonLoading,
                }]
                : []),
        ];

        return ({
            title: store.label('dashboard.locations.title'),
            actions: helpButtonProps,
            table: {
                columns: [
                    {
                        header: store.label('dashboard.locations.header.location'),
                        width: '100%',
                        isSortable: true,
                    },
                    {
                        header: store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : '',
                        isSortable: true,
                    },
                    {
                        header: store.label('dashboard.common.header.change'),
                        isSortable: true,
                    },
                    {
                        header: store.label('dashboard.common.header.activity'),
                        isSortable: true,
                    },
                ],
                rows: locationRows,
            },
            more: {showExpand: true},
        });
    }
};

const lengthOfServiceProps = (store: TeamResultsStore, userStore: UserStore): ITemplateGeneralCardProps | undefined => {
    if (store.payload!.lengthsOfService === undefined) {
        return undefined;
    } else {
        const lengthOfServiceRows = store.payload!.lengthsOfService.serviceLengths
            .map((row) => dataRow(store, row, 'service-length', userStore));

        const helpButtonProps: IButtonProps[] = [
            {
                text: '',
                color: 'white' as const,
                icon: 'info',
                onClick: (): void => store.openServiceLengthHelpModal(),
            },
            ...(userStore.payload?.hasManagerRights || userStore.payload?.isAdmin
                ? [{
                    color: 'white' as const,
                    icon: 'robot-happy',
                    onClick: async () => await store.routeToMentorForSummary('serviceLength'),
                    tooltip: store.label('mentor.service-length.summary-tooltip'),
                    isLoading: store.isTranslationButtonLoading,
                }]
                : []),
        ];

        return ({
            title: store.label('dashboard.service-length.title'),
            actions: helpButtonProps,
            table: {
                columns: [
                    {
                        header: store.label('dashboard.service-length.header'),
                        width: '100%',
                        isSortable: true,
                    },
                    {
                        header: store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : '',
                        isSortable: true,
                    },
                    {
                        header: store.label('dashboard.common.header.change'),
                        isSortable: true,
                    },
                    {
                        header: store.label('dashboard.common.header.activity'),
                        isSortable: true,
                    },
                ],
                rows: lengthOfServiceRows,
            },
            more: {showExpand: true},
        });
    }
};

const rolesProps = (store: TeamResultsStore, userStore: UserStore): ITemplateGeneralCardProps | undefined => {
    if (store.payload!.roles === undefined) {
        return undefined;
    } else {
        const roleRows = store.payload!.roles.roles
            .map((row) => dataRow(store, row, 'roles', userStore));

        const helpButtonProps: IButtonProps[] = [
            {
                text: '',
                color: 'white' as const,
                icon: 'info',
                onClick: (): void => store.openRolesHelpModal(),
            },
            ...((userStore.payload?.hasManagerRights || userStore.payload?.isAdmin) && userStore.payload?.isMentorEnabled === true
                ? [{
                    color: 'white' as const,
                    icon: 'robot-happy',
                    onClick: async () => await store.routeToMentorForSummary('role'),
                    tooltip: store.label('mentor.role.summary-tooltip'),
                    isLoading: store.isTranslationButtonLoading,
                }]
                : []),
        ];

        return ({
            title: store.label('dashboard.roles.title'),
            actions: helpButtonProps,
            table: {
                columns: [
                    {
                        header: store.label('dashboard.roles.header.role'),
                        width: '100%',
                        isSortable: true,
                    },
                    {
                        header: store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : '',
                        isSortable: true,
                    },
                    {
                        header: store.label('dashboard.common.header.change'),
                        isSortable: true,
                    },
                    {
                        header: store.label('dashboard.common.header.activity'),
                        isSortable: true,
                    },
                ],
                rows: roleRows,
            },
            more: {showExpand: true},
        });
    }
};

const tagsProps = (store: TeamResultsStore, userStore: UserStore): ITemplateGeneralCardProps | undefined => {
    if (store.payload!.tags === undefined) {
        return undefined;
    } else {
        const tagRows = store.payload!.tags.tags
            .map((row) => dataRow(store, row, 'tags', userStore));

        const helpButtonProps: IButtonProps[] = [
            {
                text: '',
                color: 'white',
                icon: 'info',
                onClick: (): void => store.openTagsHelpModal(),
            },
            ...((userStore.payload?.hasManagerRights || userStore.payload?.isAdmin) && userStore.payload?.isMentorEnabled === true
                ? [{
                    color: 'white' as const,
                    icon: 'robot-happy',
                    onClick: async () => await store.routeToMentorForSummary('tag'),
                    tooltip: store.label('mentor.tag.summary-tooltip'),
                    isLoading: store.isTranslationButtonLoading,
                }]
                : []),
        ];

        return ({
            title: store.label('dashboard.tags.title'),
            actions: helpButtonProps,
            table: {
                columns: [
                    {
                        header: store.label('dashboard.tags.header.tag'),
                        width: '100%',
                        isSortable: true,
                    },
                    {
                        header: store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : '',
                        isSortable: true,
                    },
                    {
                        header: store.label('dashboard.common.header.change'),
                        isSortable: true,
                    },
                    {
                        header: store.label('dashboard.common.header.activity'),
                        isSortable: true,
                    },
                ],
                rows: tagRows,
            },
            more: {showExpand: true},
        });
    }
};

const shouldShowCompareToTooltip = (store: TeamResultsStore, cardType: string) => {
    if (store.payload!.tooltip.businessUnitName === store.payload!.tooltip.compareToName) {
        return false;
    }
    if (cardType === 'category') {
        return true;
    }

    return (!store.payload!.tooltip.isMachineLearning);
};

const dataRow = (store: TeamResultsStore, data: DashboardData, translationKey: string, userStore: UserStore): ITemplateGeneralRowProps => {
    const score = data.barpoint.value.scorePercentage;
    const compareScore = data.barpoint.compareTo.scorePercentage;
    const allValueLabelKey = data.value === 'ALL' ? store.label(`common.${translationKey}.all`) : data.value;
    const labelKey = translationKey === 'category' ? getCategoryName(userStore.payload!.language, data.value, store.categories) : `common.${translationKey}.${data.value}`;
    const tooltipLabelKey = isCustomCategory(data.value) ? labelKey : `common.${translationKey}.description.` + getDefaultCategoryName(store, data.value);

    let scoreChangeColor: string;

    if (data.scoreChange > 0) {
        scoreChangeColor = 'green';
    } else if (data.scoreChange < 0) {
        scoreChangeColor = 'red';
    } else {
        scoreChangeColor = 'grey';
    }

    return ({
        id: data.value,
        title: translationKey === 'roles' || translationKey === 'tags' ? allValueLabelKey : store.label(labelKey),
        titleTooltip: translationKey === 'category' ? store.label(tooltipLabelKey) : undefined,
        score: {
            progress: {
                percent: score,
                color: score >= compareScore ? 'green' : 'red',
                indicator: compareScore,
            },
            indicator: {
                number: score,
                color: score >= compareScore ? 'green' : 'red',
            },
        },
        scoreTooltip: <ScoreTooltip
            period={store.payload!.tooltip.period}
            name={store.payload!.tooltip.businessUnitName}
            compareToName={shouldShowCompareToTooltip(store, translationKey) ? store.payload!.tooltip.compareToName : undefined}
            value={data.barpoint.value}
            compareTo={shouldShowCompareToTooltip(store, translationKey) ? data.barpoint.compareTo : undefined}
        />,
        change: {
            number: data.scoreChange,
            color: scoreChangeColor,
            isNumberNotVisible: true,
        },
        activity: {
            percent: data.barpoint.value.participationPercentage,
            color: 'blue',
            indicator: data.barpoint.compareTo.participationPercentage,
        },
        activityTooltip: <ScoreTooltip
            period={store.payload!.tooltip.period}
            name={store.payload!.tooltip.businessUnitName}
            compareToName={shouldShowCompareToTooltip(store, translationKey) ? store.payload!.tooltip.compareToName : undefined}
            value={data.barpoint.value}
            compareTo={shouldShowCompareToTooltip(store, translationKey) ? data.barpoint.compareTo : undefined}
        />,
        isFocused: data.focused,
    } as ITemplateGeneralRowProps);
};

const heatmapProps = (store: TeamResultsStore, userStore: UserStore): ITemplateDashboardHeatmapProps | undefined => {
    if (store.payload!.heatmap === undefined) {
        return undefined;
    } else {

        const defaultOpenItems: string[] = [];

        let openBusinessUnit = store.payload!.company!.rootBusinessUnitData;

        while (!openBusinessUnit.focused) {
            defaultOpenItems.push(openBusinessUnit.id);
            openBusinessUnit = openBusinessUnit.children[0];
        }

        defaultOpenItems.push(openBusinessUnit.id);

        const helpButtonProps: IButtonProps[] = [
            {
                text: '',
                color: 'white',
                icon: 'info',
                onClick: (): void => store.openHeatmapHelpModal(),
            },
        ];

        const rootRow = store.payload!.heatmap.isMatrixTeam ? heatmapRowProps(store, store.payload!.heatmap.rootRow, 'exclude-children', userStore, store.payload!.heatmap.rootRow.businessUnitName) :
            heatmapRowProps(store, store.payload!.heatmap.rootRow, 'exclude-children', userStore,store.label('dashboard.company.all-departments'));

        return ({
            title: store.label('dashboard.heatmap.title'),
            actions: helpButtonProps,
            heatmap: {
                columns: [{header: ''}, ...store.payload!.heatmap.categories.map((category) => ({
                    header: getShortCategoryNameForHeatMap(store, userStore.payload!.language, category),
                    tooltip: getCategoryName(userStore.payload!.language, category, store.categories),
                    isFocused: category === store.payload!.heatmap?.focusedCategory,
                }))],
                rows: [rootRow,
                    ...store.payload!.heatmap.rootRow.children.map((child) => heatmapRowProps(store, child, 'include-children', userStore))],
                useHover: true,
                defaultOpenItems,
            },
        });
    }
};

const heatmapRowProps = (store: TeamResultsStore, row: HeatmapRow, mode: 'include-children' | 'exclude-children', userStore?: UserStore, titleOverride?: string): IHeatmapRowProps => {
    const heatCells: IHeatmapCellProps[] = row.cells.map((cell) => ({
        content: {level: heatLevel(cell.datapoint)},
        tooltip: <ScoreTooltip
            period={store.payload!.tooltip.period}
            categoryName= {getCategoryName(userStore!.payload!.language, cell.category, store.categories)}
            value={cell.datapoint}
        />,
    }));

    return {
        cells: [{content: titleOverride ? titleOverride : row.businessUnitName}, ...heatCells],
        isFocused: row.focused,
        subRows: mode === 'include-children' ? row.children.map((subRow) => heatmapRowProps(store, subRow, mode, userStore)) as ITableRowProps[] : [],
    };
};


const heatLevel = (datapoint: Datapoint): THeatLevel => {
    const score = datapoint.scorePercentage;

    if (datapoint.asked === 0) {
        return 6;
    }
    if (score > 80) {
        return 1;
    }
    if (score > 60) {
        return 2;
    }
    if (score > 40) {
        return 3;
    }
    if (score > 20) {
        return 4;
    }

    return 5;
};

const companyProps = (store: TeamResultsStore, userStore: UserStore): ITemplateGeneralCardProps | undefined => {
    if (store.payload!.company === undefined) {
        return undefined;
    } else {
        const defaultOpenItems: string[] = [];

        let openBusinessUnit = store.payload!.company.rootBusinessUnitData;

        while (!openBusinessUnit.focused) {
            defaultOpenItems.push(openBusinessUnit.id);
            openBusinessUnit = openBusinessUnit.children[0];
        }

        defaultOpenItems.push(openBusinessUnit.id);

        const helpButtonProps: IButtonProps[] = [
            {
                text: '',
                color: 'white',
                icon: 'info',
                onClick: (): void => store.openCompanyHelpModal(),
            },
            ...((userStore.payload?.hasManagerRights || userStore.payload?.isAdmin) && userStore.payload?.isMentorEnabled === true
                ? [{
                    color: 'white' as const,
                    icon: 'robot-happy',
                    onClick: async () => await store.routeToMentorForSummary('department'),
                    tooltip: store.label('mentor.department.summary-tooltip'),
                    isLoading: store.isTranslationButtonLoading,
                }]
                : []),
        ];


        const rootBusinessUnitRow = store.payload!.company.isMatrixTeam ? businessUnitRow(store, store.payload!.company.rootBusinessUnitData, 'exclude-children', store.payload!.company.rootBusinessUnitData.title) :
            businessUnitRow(store, store.payload!.company.rootBusinessUnitData, 'exclude-children', store.label('dashboard.company.all-departments'));

        return ({
            title: store.label('dashboard.company.title'),
            actions: helpButtonProps,
            table: {
                columns: [
                    {
                        header: store.label('dashboard.company.header.department'),
                        width: '100%',
                        isSortable: true,
                    },
                    {
                        header: store.variables.periodId ? formatDateRange(rangeFromPeriodId(store.variables.periodId)) : '',
                        isSortable: true,
                    },
                    {
                        header: store.label('dashboard.common.header.change'),
                        isSortable: true,
                    },
                    {
                        header: store.label('dashboard.common.header.activity'),
                        isSortable: true,
                    },
                ],
                rows: store.payload!.overview!.total.answered < 50 ? {
                    type: 'info',
                    text: store.label('dashboard.not-enough-data'),
                } : [
                    rootBusinessUnitRow,
                    ...store.payload!.company.rootBusinessUnitData.children.map((child) => businessUnitRow(store, child, 'include-children')),
                ],
                defaultOpenItems,
            },
            more: {showExpand: true},
        });
    }
};

const businessUnitRow = (store: TeamResultsStore, businessUnitData: BusinessUnitData, mode: 'include-children' | 'exclude-children', titleOverride?: string): ITemplateGeneralRowProps => {
    const score = businessUnitData.barpoint.value.scorePercentage;
    const compareScore = businessUnitData.barpoint.compareTo.scorePercentage;

    let scoreChangeColor: string;

    if (businessUnitData.scoreChange > 0) {
        scoreChangeColor = 'green';
    } else if (businessUnitData.scoreChange < 0) {
        scoreChangeColor = 'red';
    } else {
        scoreChangeColor = 'grey';
    }

    return ({
        id: businessUnitData.id,
        title: titleOverride === undefined ? businessUnitData.title : titleOverride,
        score: {
            progress: {
                percent: score,
                color: score >= compareScore ? 'green' : 'red',
                indicator: compareScore,
            },
            indicator: {
                number: score,
                color: score >= compareScore ? 'green' : 'red',
            },
        },
        scoreTooltip: <ScoreTooltip
            period={store.payload!.tooltip.period}
            name={businessUnitData.title}
            compareToName={store.payload!.tooltip.compareToName}
            value={businessUnitData.barpoint.value}
            compareTo={businessUnitData.barpoint.compareTo}
        />,
        change: {
            number: businessUnitData.scoreChange,
            color: scoreChangeColor,
            isNumberNotVisible: true,
        },
        activity: {
            percent: businessUnitData.barpoint.value.participationPercentage,
            color: 'blue',
            indicator: businessUnitData.barpoint.compareTo.participationPercentage,
        },
        activityTooltip: <ScoreTooltip
            period={store.payload!.tooltip.period}
            name={businessUnitData.title}
            compareToName={store.payload!.tooltip.compareToName}
            value={businessUnitData.barpoint.value}
            compareTo={businessUnitData.barpoint.compareTo}
        />,
        isFocused: businessUnitData.focused,
        subItems: mode === 'include-children' ? businessUnitData.children.map((child) => businessUnitRow(store, child, 'include-children')) : [],
    } as ITemplateGeneralRowProps);
};

const questionProps = (store: TeamResultsStore, userStore: UserStore): ITemplateDashboardQuestionsProps | undefined => {
    if (store.payload!.questions === undefined || store.payload!.questions.questions.length === 0) {
        return undefined;
    } else {
        const visibleQuestionCards = store.visibleQuestionCards().map((question) => questionCardProps(store, userStore, question));

        const helpButtonProps: IButtonProps[] = [
            {
                text: '',
                color: 'white',
                icon: 'info',
                onClick: (): void => store.openQuestionsHelpModal(),
            },
        ];

        return (
            {
                title: store.label('questions.title'),
                actions: helpButtonProps,
                infoButton: {
                    text: store.label('company.actions.help'),
                    iconLeft: 'info',
                },
                items: visibleQuestionCards,
                button: store.shouldShowLoadMoreButton() ? {
                    text: store.label('dashboard.questions.load-more'),
                    onClick: (): void => store.loadMoreQuestionCards(),
                } : undefined,
            });
    }
};

const adHocQuestionProps = (store: TeamResultsStore, userStore: UserStore): ITemplateDashboardQuestionsProps | undefined => {
    if (store.payload!.adHocQuestions === undefined || store.payload!.adHocQuestions.questions.length === 0) {
        return undefined;
    } else {

        const helpButtonProps: IButtonProps[] = [
            {
                text: '',
                color: 'white',
                icon: 'info',
                onClick: (): void => store.openQuestionsHelpModal(),
            },
        ];

        return ({
            title: store.label('questions.title'),
            actions: helpButtonProps,
            infoButton: {
                text: store.label('company.actions.help'),
                iconLeft: 'info',
            },
            items: store.payload!.adHocQuestions.questions.map((question) => questionCardProps(store, userStore, question)),
        });
    }
};

const questionCardProps = (store: TeamResultsStore, userStore: UserStore, question: QuestionCard): IQuestionProps => {
    const score = question.barpoint.value.scorePercentage;
    const activity = question.barpoint.value.participationPercentage;

    const chartScores = question.chartScores.length === 1 ? [question.chartScores[0], question.chartScores[0]] : question.chartScores;

    const answers = question.feedbackAnswers.map((answer: AnswerWithCommentOnQuestionCard): IAnswerProps => {
        const canManagerClickHasSeen = userStore.payload?.hasManagerRights && userStore.payload!.businessUnitId === answer.questionBusinessUnitId;
        const checkIsVisibleToManagersAndAdmins = canManagerClickHasSeen || userStore.payload?.isAdmin ? store.hasSeen(answer) : undefined;
        const checkIsVisible = store.hasSeen(answer) ? store.hasSeen(answer) : undefined;

        return ({
            reactionType: answer.answer === 'null' ? 'talk-bubble' : 'thumb',
            direction: answer.answer === '100' || answer.answer === '75' ? 'up' : 'down',
            text: answer.text,
            onHasSeenCheck: canManagerClickHasSeen || userStore.payload?.isAdmin ? (checked): void => {
                store.managerChecksComment(answer.questionId, checked);
            } : undefined,
            hasSeen: checkIsVisibleToManagersAndAdmins !== undefined ? checkIsVisibleToManagersAndAdmins : checkIsVisible,
            titleTooltip: store.hasSeen(answer) ? store.label('dashboard.feedback.manager-has-seen') : store.label('dashboard.feedback.manager-should-see'),
        });
    });

    const mentorButtonProps: IButtonProps = {
        text: store.label('dashboard.questions.ask-mentor'),
        onClick: async (): Promise<void> => {
            await store.routeToMentor(question);
        },
    };

    const isManagerOrAdmin: boolean = userStore.payload?.hasManagerRights === true || userStore.payload?.isAdmin === true;
    const compareToResultScore = question.barpoint.compareTo.scorePercentage;

    return ({
        category: question.categoryId ? getCategoryName(userStore.payload!.language, question.categoryId, store.categories) : store.label(`common.category.ad-hoc`),
        text: question.questionText,
        answers: {answers},
        bars: [
            {
                icon: 'users',
                color: 'blue' as TProgressColor,
                label: store.label('dashboard.questions.activity', {
                    number_of_answers: question.barpoint.value.answered.toString(),
                    number_of_questions_asked: question.barpoint.value.prepared.toString(),
                }),
                percent: activity,
            },
            ...(question.barpoint.value.onlyFreeTextAnswers ? [] : [{
                icon: 'like',
                color: (score > 50 ? 'green' : 'red') as TProgressColor,
                label: store.label('dashboard.questions.score', { score_percentage: score.toString() }),
                percent: score,
            }]),
        ],
        chart: {
            id: 'chart-1',
            data: chartScores.map((chartScore: number, index: number): IChartDataProps => ({
                name: index + 1,
                a: chartScore,
            })),
            areas: [
                {
                    key: 'a',
                    color: question.notEnoughDataCompareToDatapoint ? 'yellowPastel' : score >= compareToResultScore ? 'greenPastel' : 'redPastel',
                    strokeWidth: 1,
                    lineColor: question.notEnoughDataDatapoint || question.barpoint.value.onlyFreeTextAnswers ? 'gray' : score >= compareToResultScore ? 'green' : 'red',
                },
            ],
            showTooltip: false,
            aspect: 6.57,
            hideDots: true,
            hideGrid: true,
            hideXAxis: true,
            hideYAxis: true,
        },
        hasMentor: isManagerOrAdmin ? mentorButtonProps : undefined,
        titleTooltip: userStore.payload?.isMentorEnabled !== true ? store.label('dashboard.mentor-upsell-cta') : undefined,
    });
};

const tooFewEmployeesInFilterSelectionAlert = (store: TeamResultsStore): ITemplatePlainTextCardProps | undefined => {
    if (store.payload!.overview !== undefined) {
        return undefined;
    } else {
        return ({
            text: {
                rows: {
                    text: store.label('dashboard.too-few-employees-in-filter-alert'),
                    type: 'info',
                },
            },
        });
    }
};

